// Voice of Partner — sidebar "TrustLab Feedback" page.
//
// Reads from `useGetActiveFeedbackQuery()` and renders one of three
// states based on the response:
//
//   1. cycle === null            → "No active feedback cycle" empty
//                                   state. Backend has either never
//                                   opened a cycle or the most-recent
//                                   one auto-closed.
//
//   2. cycle && !hasSubmitted    → yellow cycle banner + the
//                                   PartnerFeedbackForm. Submitting
//                                   invalidates the 'PartnerFeedback'
//                                   tag → this query refetches →
//                                   `hasSubmitted` flips to true →
//                                   page swaps to state 3.
//
//   3. cycle && hasSubmitted     → green thank-you recap. The recap
//                                   tiles come from `mySubmission`
//                                   (the partner's recorded answers).
//
// Plus a fourth fallback for ineligible accounts (user_type isn't
// doctor / lab_partner) — those shouldn't see the menu entry, but
// if they navigate here directly we surface a polite "not available
// for your account" line instead of crashing.
import { useState } from 'react';
import Box from '@mui/material/Box';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import CircularProgress from '@mui/material/CircularProgress';
import Snackbar from '@mui/material/Snackbar';
import MuiAlert from '@mui/material/Alert';
import CalendarMonthOutlinedIcon from '@mui/icons-material/CalendarMonthOutlined';
import CheckRoundedIcon from '@mui/icons-material/CheckRounded';
import AccessTimeOutlinedIcon from '@mui/icons-material/AccessTimeOutlined';
import LockOutlinedIcon from '@mui/icons-material/LockOutlined';
import { MP, MONO, DISPLAY } from '../medical/shared';
import {
  formatCloseDate,
  getCountdown,
  avgOf,
  npsBand,
  CADENCE_LABEL,
} from './state';
import { useGetActiveFeedbackQuery } from '../../../store/feedbackApi';
import { useAuth } from '../../../context/AuthContext';
import PartnerFeedbackForm from './PartnerFeedbackForm';

const PartnerFeedbackPage = () => {
  const { user } = useAuth();
  const { data, isLoading } = useGetActiveFeedbackQuery();
  const [toast, setToast] = useState({ open: false, msg: '' });

  const cycle = data?.cycle || null;
  const hasSubmitted = !!data?.hasSubmitted;
  const mySubmission = data?.mySubmission || null;
  const eligible = data?.eligible !== false;
  const isOpen = !!cycle && cycle.status === 'open';

  // Org card values come from the authed user — no separate fetch.
  const org = {
    name:    user?.name || 'Partner',
    id:      user?.client_id || `USR-${String(user?._id || '').slice(-6).toUpperCase()}`,
    contact: user?.email || user?.phone || '',
  };

  const onSubmitted = () => {
    // The mutation invalidated the active query, which will refetch
    // with hasSubmitted=true → the JSX below swaps to the thank-you
    // state. Toast just gives an immediate confirmation.
    setToast({ open: true, msg: 'Submitted. Routed to leadership.' });
  };

  return (
    <Box>
      <PageHeader
        org={org}
        loading={isLoading}
        eligible={eligible}
        cycleStatus={cycle?.status}
        submitted={hasSubmitted}
      />

      {/* Loading state — the active query usually resolves fast, but
          while it's in flight render a quiet spinner so we don't
          flash the "no cycle" empty state at users who actually
          have a form to fill in. */}
      {isLoading && (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
          <CircularProgress size={28} sx={{ color: MP.teal }} />
        </Box>
      )}

      {/* Ineligible — surfaced ahead of the cycle states so a stray
          patient / lab-staff account doesn't get a confusing form. */}
      {!isLoading && !eligible && <IneligibleState />}

      {/* State 1: cycle null → empty-state card */}
      {!isLoading && eligible && !cycle && <ClosedState />}

      {/* State 2: cycle open, not submitted → form */}
      {!isLoading && eligible && isOpen && !hasSubmitted && (
        <>
          <CycleBanner cycle={cycle} />
          <PartnerFeedbackForm onSubmitted={onSubmitted} />
        </>
      )}

      {/* State 3: cycle open, already submitted → thank-you recap */}
      {!isLoading && eligible && isOpen && hasSubmitted && (
        <SubmittedState
          cycleId={cycle.cycle_id}
          orgId={org.id}
          submission={mySubmission}
        />
      )}

      <Snackbar
        open={toast.open}
        autoHideDuration={3500}
        onClose={() => setToast((t) => ({ ...t, open: false }))}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <MuiAlert
          onClose={() => setToast((t) => ({ ...t, open: false }))}
          severity="success"
          variant="filled"
          sx={{ fontWeight: 600 }}
          icon={<CheckRoundedIcon fontSize="small" />}
        >
          {toast.msg}
        </MuiAlert>
      </Snackbar>
    </Box>
  );
};

// ─── Page header ────────────────────────────────────────────────
// Org tile on the left, cycle-status chip on the right — matches the
// prototype's `client-header`. Pill colour-codes the active state so
// the partner can tell at a glance whether action is needed.
const PageHeader = ({ org, cycleStatus, submitted, loading, eligible }) => {
  const initials = String(org.name || 'P').split(' ').map((w) => w[0]).slice(0, 2).join('').toUpperCase();

  let pillLabel = 'No Active Cycle';
  let pillTones = { bg: MP.bg, fg: MP.textMuted, border: MP.border };
  if (loading) {
    pillLabel = 'Checking…';
  } else if (!eligible) {
    pillLabel = 'Not Available';
  } else if (cycleStatus === 'open' && !submitted) {
    pillLabel = 'Feedback Active';
    pillTones = { bg: MP.greenLight, fg: MP.green, border: MP.green };
  } else if (cycleStatus === 'open' && submitted) {
    pillLabel = 'Already Submitted';
    pillTones = { bg: MP.tealLight, fg: MP.tealDark, border: MP.teal };
  }

  return (
    <Box
      sx={{
        bgcolor: '#fff',
        border: `1px solid ${MP.border}`,
        borderRadius: '14px',
        px: { xs: 2.5, md: 3 },
        py: 2.25,
        mb: 2.25,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        gap: 2,
        flexWrap: 'wrap',
      }}
    >
      <Stack direction="row" alignItems="center" gap={1.75}>
        <Box
          sx={{
            width: 44, height: 44,
            bgcolor: MP.yellowLight,
            border: `1.5px solid ${MP.yellow}`,
            borderRadius: '50%',
            display: 'grid',
            placeItems: 'center',
            fontFamily: DISPLAY,
            fontSize: 16,
            fontWeight: 800,
            color: MP.dark,
            letterSpacing: '0.04em',
          }}
        >
          {initials}
        </Box>
        <Box>
          <Typography sx={{ fontWeight: 700, fontSize: 15, color: MP.dark }}>
            {org.name}
          </Typography>
          <Typography sx={{ fontFamily: MONO, fontSize: 12, color: MP.textMuted, letterSpacing: '0.04em' }}>
            {org.id} · {org.contact}
          </Typography>
        </Box>
      </Stack>

      <Box
        sx={{
          display: 'inline-flex',
          alignItems: 'center',
          gap: 0.875,
          fontFamily: MONO,
          fontSize: 11,
          letterSpacing: '0.08em',
          textTransform: 'uppercase',
          fontWeight: 700,
          px: 1.25, py: 0.625,
          borderRadius: 99,
          bgcolor: pillTones.bg,
          color: pillTones.fg,
          border: `1px solid ${pillTones.border}`,
        }}
      >
        <Box sx={{ width: 6, height: 6, borderRadius: '50%', bgcolor: 'currentColor' }} />
        {pillLabel}
      </Box>
    </Box>
  );
};

// ─── State 1: cycle closed ──────────────────────────────────────
const ClosedState = () => (
  <Box
    sx={{
      bgcolor: '#fff',
      border: `1px dashed ${MP.border}`,
      borderRadius: '14px',
      px: { xs: 3, md: 4 },
      py: { xs: 5, md: 6 },
      textAlign: 'center',
    }}
  >
    <Box
      sx={{
        width: 64, height: 64,
        mx: 'auto', mb: 2,
        bgcolor: MP.bg,
        borderRadius: '50%',
        display: 'grid',
        placeItems: 'center',
        color: MP.textMuted,
      }}
    >
      <CalendarMonthOutlinedIcon sx={{ fontSize: 32 }} />
    </Box>
    <Typography
      sx={{
        fontFamily: DISPLAY,
        fontSize: { xs: 22, md: 26 },
        fontWeight: 700,
        letterSpacing: '0.04em',
        color: MP.dark,
        mb: 1,
        textTransform: 'uppercase',
      }}
    >
      No active feedback cycle
    </Typography>
    <Typography
      sx={{
        fontSize: 14,
        color: MP.textMuted,
        maxWidth: '44ch',
        mx: 'auto',
      }}
    >
      The next feedback cycle will open soon. We'll notify your registered email and surface a banner here when it's live.
    </Typography>
  </Box>
);

// ─── Fallback for non-eligible accounts ─────────────────────────
// Reached when a patient or unrelated user navigates to the menu
// item directly. The sidebar should already hide the entry for
// these accounts, but the fallback keeps the route bullet-proof.
const IneligibleState = () => (
  <Box
    sx={{
      bgcolor: '#fff',
      border: `1px dashed ${MP.border}`,
      borderRadius: '14px',
      px: { xs: 3, md: 4 },
      py: { xs: 5, md: 6 },
      textAlign: 'center',
    }}
  >
    <Box
      sx={{
        width: 64, height: 64,
        mx: 'auto', mb: 2,
        bgcolor: MP.bg,
        borderRadius: '50%',
        display: 'grid',
        placeItems: 'center',
        color: MP.textMuted,
      }}
    >
      <LockOutlinedIcon sx={{ fontSize: 28 }} />
    </Box>
    <Typography
      sx={{
        fontFamily: DISPLAY,
        fontSize: { xs: 22, md: 26 },
        fontWeight: 700,
        letterSpacing: '0.04em',
        color: MP.dark,
        mb: 1,
        textTransform: 'uppercase',
      }}
    >
      Not available for your account
    </Typography>
    <Typography
      sx={{
        fontSize: 14,
        color: MP.textMuted,
        maxWidth: '46ch',
        mx: 'auto',
      }}
    >
      Voice of Partner is open to registered doctors and lab partners. If you should have access, contact your account manager and we'll update your account type.
    </Typography>
  </Box>
);

// ─── State 2 banner ─────────────────────────────────────────────
// `cycle.deadline` arrives as an ISO date string from the API; the
// formatters expect a numeric timestamp, so we convert once.
const CycleBanner = ({ cycle }) => {
  const cadence = CADENCE_LABEL[cycle.cadence] || cycle.cadence;
  const deadlineMs = cycle.deadline ? new Date(cycle.deadline).getTime() : null;
  const closeFmt = deadlineMs ? formatCloseDate(deadlineMs) : '—';
  const countdown = deadlineMs ? getCountdown(deadlineMs) : '';
  return (
    <Box
      sx={{
        bgcolor: MP.yellowLight,
        border: `1.5px solid ${MP.yellow}`,
        borderRadius: '14px',
        px: { xs: 2.25, md: 3 },
        py: 2.25,
        mb: 2.25,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        gap: 2,
        flexWrap: 'wrap',
      }}
    >
      <Box>
        <Typography
          sx={{
            fontFamily: DISPLAY,
            fontSize: 18,
            letterSpacing: '0.04em',
            fontWeight: 700,
            color: MP.dark,
            mb: 0.25,
          }}
        >
          {cycle.name}
        </Typography>
        <Typography sx={{ fontSize: 14, color: MP.dark }}>
          Your inputs shape our service this {cadence}.
        </Typography>
      </Box>
      <Stack
        direction="row"
        alignItems="center"
        gap={0.75}
        sx={{
          fontFamily: MONO,
          fontSize: 11,
          letterSpacing: '0.04em',
          color: MP.dark,
        }}
      >
        <AccessTimeOutlinedIcon sx={{ fontSize: 16, color: MP.dark }} />
        <Box>
          Closes <Box component="strong" sx={{ fontWeight: 700 }}>{closeFmt}</Box>
          <Box component="span" sx={{ mx: 0.5 }}>·</Box>
          {countdown}
          <Box component="span" sx={{ mx: 0.5 }}>·</Box>
          ~3 mins
        </Box>
      </Stack>
    </Box>
  );
};

// ─── State 3: thank-you recap ───────────────────────────────────
// `submission` is the FeedbackResponse the backend returned. The
// `csat`/`ces` fields come back as plain objects (Mongoose Maps
// serialise to JSON objects), so `avgOf` works on them directly.
const SubmittedState = ({ cycleId, orgId, submission }) => {
  const csatAvg = avgOf(submission?.csat);
  const cesAvg  = avgOf(submission?.ces);
  const nps     = submission?.nps;
  // Backend stamps `createdAt` (timestamps option) on every response
  // doc — that's what the prototype called `submittedAt`.
  const submittedTs = submission?.createdAt || submission?.submittedAt;
  const submittedOn = submittedTs
    ? new Date(submittedTs).toLocaleString('en-IN', {
        day: '2-digit', month: 'short', year: 'numeric',
        hour: '2-digit', minute: '2-digit', hour12: true,
      })
    : null;

  return (
    <Box
      sx={{
        bgcolor: MP.greenLight,
        border: `1px solid ${MP.green}`,
        borderRadius: '14px',
        px: { xs: 3, md: 4 },
        py: { xs: 4, md: 5 },
        textAlign: 'center',
      }}
    >
      <Box
        sx={{
          width: 64, height: 64,
          mx: 'auto', mb: 2,
          bgcolor: '#fff',
          border: `2px solid ${MP.green}`,
          borderRadius: '50%',
          display: 'grid',
          placeItems: 'center',
          color: MP.green,
        }}
      >
        <CheckRoundedIcon sx={{ fontSize: 32 }} />
      </Box>
      <Typography
        sx={{
          fontFamily: DISPLAY,
          fontSize: { xs: 22, md: 26 },
          fontWeight: 700,
          letterSpacing: '0.04em',
          color: MP.dark,
          mb: 1,
          textTransform: 'uppercase',
        }}
      >
        Thank you — your feedback is in.
      </Typography>
      <Typography
        sx={{
          fontSize: 14,
          color: MP.text,
          maxWidth: '52ch',
          mx: 'auto',
          mb: 3,
        }}
      >
        We've routed your inputs to the relevant team. The branch lead will reach out within 5 working days if there are open items requiring follow-up.
      </Typography>

      {/* Quick recap of what they shared */}
      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: { xs: '1fr', sm: 'repeat(3, 1fr)' },
          gap: 1.5,
          maxWidth: 560,
          mx: 'auto',
          mb: 2.5,
        }}
      >
        <RecapTile
          label="CSAT"
          value={csatAvg != null ? `${csatAvg.toFixed(1)} / 5` : '—'}
          tone={MP.teal}
        />
        <RecapTile
          label="NPS"
          value={nps != null ? `${nps >= 0 ? '+' : ''}${nps} · ${npsBand(nps)}` : '—'}
          tone={MP.yellow}
        />
        <RecapTile
          label="CES"
          value={cesAvg != null ? `${cesAvg.toFixed(1)} / 7` : '—'}
          tone={MP.purple}
        />
      </Box>

      <Box
        sx={{
          display: 'inline-block',
          fontFamily: MONO,
          fontSize: 11,
          letterSpacing: '0.08em',
          color: MP.textMuted,
          bgcolor: '#fff',
          px: 1.75, py: 1,
          borderRadius: '8px',
          border: `1px dashed ${MP.border}`,
        }}
      >
        REF · {cycleId}-{orgId}{submittedOn ? ` · ${submittedOn}` : ''}
      </Box>
    </Box>
  );
};

const RecapTile = ({ label, value, tone }) => (
  <Box
    sx={{
      bgcolor: '#fff',
      border: `1px solid ${MP.border}`,
      borderRadius: '8px',
      py: 1.5, px: 1.5,
      position: 'relative',
      overflow: 'hidden',
      '&::before': {
        content: '""',
        position: 'absolute',
        top: 0, left: 0,
        height: 3, width: 40,
        bgcolor: tone,
      },
    }}
  >
    <Typography
      sx={{
        fontFamily: MONO,
        fontSize: 10,
        letterSpacing: '0.14em',
        textTransform: 'uppercase',
        color: MP.textMuted,
        fontWeight: 700,
        mb: 0.5,
      }}
    >
      {label}
    </Typography>
    <Typography
      sx={{
        fontFamily: DISPLAY,
        fontSize: 18,
        letterSpacing: '0.04em',
        color: MP.dark,
        fontWeight: 700,
      }}
    >
      {value}
    </Typography>
  </Box>
);

export default PartnerFeedbackPage;
